import { useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { Button, Card, CardSubtitle, CardTitle, Field, Input, Wordmark } from '@keobook/ui';
import { useAuth } from '../lib/auth';

/**
 * Standard "request a reset link" screen. The non-enumerating confirmation
 * (same message whether or not the email exists) is the correct UX — we show
 * it even if the SDK call throws, so the response never reveals whether the
 * address is registered.
 */
export function ForgotPassword() {
  const { resetPassword } = useAuth();
  const [email, setEmail] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setSubmitting(true);
    try {
      await resetPassword(email.trim());
    } catch {
      /* swallow — never reveal whether the address exists */
    } finally {
      setSubmitting(false);
      setSubmitted(true);
    }
  }

  return (
    <main className="kb-auth-page">
      <div className="kb-auth-container">
        <div style={{ textAlign: 'center', marginBottom: '2rem' }}>
          <Wordmark size="lg" />
        </div>

        <Card size="xl">
          {submitted ? (
            <>
              <CardTitle>Check your email</CardTitle>
              <CardSubtitle>
                If an account exists for <strong>{email}</strong>, we've sent password-reset
                instructions. Check your inbox and spam folder.
              </CardSubtitle>
              <p className="kb-auth-switch">
                <Link to="/signin">← Back to sign in</Link>
              </p>
            </>
          ) : (
            <>
              <CardTitle>Reset your password</CardTitle>
              <CardSubtitle>
                Enter your email and we'll send a link to reset your password.
              </CardSubtitle>
              <form onSubmit={onSubmit} noValidate>
                <Field label="Email address" required>
                  <Input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.currentTarget.value)}
                    autoComplete="email"
                    required
                    placeholder="you@example.com"
                  />
                </Field>
                <Button block type="submit" disabled={submitting}>
                  {submitting ? 'Sending…' : 'Send reset link'}
                </Button>
              </form>
              <p className="kb-auth-switch">
                <Link to="/signin">← Back to sign in</Link>
              </p>
            </>
          )}
        </Card>
      </div>
    </main>
  );
}
